import {LocalStorage} from '../storage/local-storage';
import {AppStateService} from "../app.state.service";
import {Observable} from "rxjs";
import {UserStore} from "../model/user.store";
import {Store} from "@ngrx/store";
import {Optional} from "@angular/core";
import {AppStore} from "../model/app-store.model";
import { LocalStorageConstant } from '../constants/user.storage.const';
import {UserLogged} from "../../../payloads/UserLogged";
import {jwtDecode} from "jwt-decode";

export class UserState {
  private _userObservable: Observable<UserStore>;
  private _userState: UserStore;

  constructor(@Optional() private appState:AppStateService, private store: Store<AppStore>) {
    this._userObservable = this.store.select('user');

    this._userObservable.subscribe((user) => {
      this._userState = user;
    });
  }

  get isUserAuthenticated(): boolean {
    if (!this.appState.isBrowser) {
      return false;
    }
    const token = LocalStorage.getToken();
    if (!token) {
      return false;
    }
    try {
      const decodedToken: any = UserState.jwt_decode(token + '');
      if (decodedToken?.exp == null) {
        /** Token without exp: still treat as signed-in; API returns 401 if invalid. */
        return true;
      }
      return Date.now() < decodedToken.exp * 1000;
    } catch {
      return false;
    }
  }

  set userId(userIdValue: string) {
    if (userIdValue) {
      LocalStorage.setLocalString(LocalStorageConstant.USER_ID, userIdValue);
      LocalStorage.setLocalString(LocalStorageConstant.USER_AUTH, userIdValue);
    }
  }

  get userId(): string {
    return LocalStorage.getLocalString(LocalStorageConstant.USER_ID);
  }

  get userDetails(): UserLogged {
    const encoded = LocalStorage.getLocalString(LocalStorageConstant.USER_DETAILS);
    if (!encoded) {
      return null;
    }
    try {
      return JSON.parse(UserState.decodeUtf8Base64(encoded));
    } catch {
      return null;
    }
  }

  set userDetails(user: UserLogged) {
    LocalStorage.setLocalString(
      LocalStorageConstant.USER_DETAILS,
      UserState.encodeUtf8Base64(JSON.stringify(user)),
    );
  }

  set companyUID(value) {
    LocalStorage.setRawString('companyUID', value);
  }

  get companyUID() {
    return LocalStorage.getRawString('companyUID');
  }

  set username(username: string) {
    LocalStorage.setLocalString(LocalStorageConstant.USER_USERNAME, username);
  }

  get isRememberMe(): boolean {
    return LocalStorage.getLocalString(LocalStorageConstant.USER_IS_REMEMBER_ME) == 'YES';
  }

  set isRememberMe(isRememberMe: boolean) {
    LocalStorage.setLocalString(LocalStorageConstant.USER_IS_REMEMBER_ME, isRememberMe == true ? 'YES' : 'NO'
    );
  }

  private static jwt_decode(token: string) {
    return jwtDecode(token)
  }

  /** Base64 (btoa/atob) is Latin1-only; UTF-8 bytes avoid InvalidCharacterError for names/emails with Unicode. */
  private static encodeUtf8Base64(utf8Text: string): string {
    const bytes = new TextEncoder().encode(utf8Text);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    return btoa(binary);
  }

  private static decodeUtf8Base64(b64: string): string {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  }

}
